import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { getEventParticipants, removeParticipantFromEvent } from "../../../../Services/Events";
import { toast } from "react-toastify";

// TDGE-188: event participant list with an admin-only "Remove from event"
// action. Removal deletes the user's bets/settleups/participation for this
// event only — their account and other events are untouched.
const Participants = ({ eventId, onParticipantRemoved }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    fetchParticipants();
  }, [eventId]);

  const fetchParticipants = async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      const response = await getEventParticipants(eventId);
      if (response.data.status === 1) {
        setParticipants(response.data.data.responseData);
      } else {
        toast.error(response.data.message || "Failed to fetch participants");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error fetching participants");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRemove = () => {
    if (!removeTarget || removing) return;
    setRemoving(true);
    removeParticipantFromEvent({ event_id: eventId, user_id: removeTarget.user_id })
      .then((res) => {
        if (res.data.status === 1) {
          toast.success("Participant removed from event");
          setRemoveTarget(null);
          fetchParticipants();
          if (onParticipantRemoved) onParticipantRemoved();
        } else {
          toast.error(res.data.message || "Failed to remove participant");
        }
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || "Error removing participant");
      })
      .finally(() => setRemoving(false));
  };

  if (loading) {
    return <div className="text-center p-4">Loading participants...</div>;
  }

  return (
    <>
      <div className="page_header d-flex justify-content-between align-items-center">
        <h2>Participants ({participants.length})</h2>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Bets</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {participants.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center">No participants yet</td>
              </tr>
            ) : (
              participants.map((p) => (
                <tr key={p.user_id}>
                  <td>{p.full_name}</td>
                  <td>{p.email}</td>
                  <td>{p.bets_count}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => setRemoveTarget(p)}
                    >
                      Remove from event
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal show={!!removeTarget} onHide={() => setRemoveTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove participant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {removeTarget && (
            <>
              <p>
                Remove <strong>{removeTarget.full_name}</strong> from this event?
                This will delete <strong>{removeTarget.bets_count}</strong>{" "}
                {removeTarget.bets_count === 1 ? "bet" : "bets"} and any settle-ups
                they have in this event. Their account and other events are not affected.
              </p>
              <p className="text-muted mb-0">
                Note: anyone with the event code can still rejoin.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRemoveTarget(null)} disabled={removing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmRemove} disabled={removing}>
            {removing ? "Removing..." : "Remove"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Participants;
